// Types for the Go API auth layer — hand-written from the backend contracts.
// Do NOT import from legacy auth types.

// ---- Envelope ---------------------------------------------------------------

export interface ApiEnvelope<T> {
  success: boolean;
  data?: T;
  error?: { code: string; message: string };
  message?: string;
}

export interface PaginatedEnvelope<T> extends ApiEnvelope<T[]> {
  meta?: { total: number; page: number; per_page: number };
}

// ---- Auth shapes ------------------------------------------------------------

export interface TokenPair {
  access_token: string;
  refresh_token: string;
  token_type: string;
  expires_in: number;
}

export interface OrgOption {
  org_id: string;
  code: string;
  name: string;
}

export interface AuthenticatedUser {
  id: string;
  kind: 'staff' | 'parent';
  org_id: string;
  full_name: string;
}

export type LoginStatus = 'authenticated' | 'org_selection_required';

export interface LoginResult {
  status: LoginStatus;
  tokens?: TokenPair;
  user?: AuthenticatedUser;
  orgs?: OrgOption[];
  selection_token?: string;
}

export interface OTPRequestResult {
  request_id: string;
  expires_at: string;
  dev_code?: string;
}

// ---- /me shapes -------------------------------------------------------------

export interface RoleInfo {
  code: string;
  name: string;
  school_id?: string | null;
  department_id?: string | null;
}

// The `user` field is `any` on the backend; model what we actually render.
export interface StaffUser {
  id: string;
  full_name: string;
  username?: string;
  email?: string;
  phone?: string;
  is_active?: boolean;
}

export interface MeResult {
  kind: 'staff' | 'parent';
  org_id: string;
  user: StaffUser;
  school_ids: string[];
  permissions: string[];
  roles: RoleInfo[];
  children?: unknown[];
}

// ---- Error type -------------------------------------------------------------

export class ApiError extends Error {
  /** HTTP status code (0 when the error was not from an HTTP response). */
  status: number;

  constructor(
    public readonly code: string,
    message: string,
    /** HTTP status from the response, e.g. 401, 403, 422. Defaults to 0. */
    status = 0,
  ) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

// ---- School (from /api/v1/schools) ------------------------------------------

export interface School {
  id: string;
  org_id: string;
  code: string;
  name: string;
  is_active: boolean;
  created_at: string;
}

// ---- Students ---------------------------------------------------------------

export interface Student {
  id: string;
  org_id: string;
  admission_number: string;
  full_name: string;
  dob: string;
  gender?: string | null;
  blood_group?: string | null;
  nationality?: string | null;
  house_id?: string | null;
  photo_url?: string | null;
  status: 'active' | 'alumni' | 'transferred' | 'withdrawn';
  is_rte: boolean;
  is_scholarship: boolean;
  is_bpl: boolean;
  is_staff_ward: boolean;
  created_at: string;
  updated_at: string;
}

export interface CreateStudentInput {
  admission_number: string;
  full_name: string;
  dob: string;
  gender?: string;
  blood_group?: string;
  nationality?: string;
  house_id?: string;
  is_rte?: boolean;
  is_scholarship?: boolean;
  is_bpl?: boolean;
  is_staff_ward?: boolean;
}

export interface UpdateStudentInput {
  full_name?: string;
  dob?: string;
  gender?: string;
  blood_group?: string;
  nationality?: string;
  house_id?: string;
  photo_url?: string;
  status?: 'active' | 'alumni' | 'transferred' | 'withdrawn';
  is_rte?: boolean;
  is_scholarship?: boolean;
  is_bpl?: boolean;
  is_staff_ward?: boolean;
}

// ---- Academic structures ----------------------------------------------------

export interface AcademicYear {
  id: string;
  school_id: string;
  name: string;
  starts_on: string;
  ends_on: string;
  is_current: boolean;
  created_at: string;
}

export interface Class {
  id: string;
  school_id: string;
  name: string;
  level: number;
  created_at: string;
}

export interface Section {
  id: string;
  school_id: string;
  class_id: string;
  academic_year_id: string;
  name: string;
  capacity?: number | null;
  class_teacher_staff_id?: string | null;
  created_at: string;
}

// ---- Pagination helpers -----------------------------------------------------

export interface PageMeta {
  total: number;
  page: number;
  per_page: number;
}

export interface PagedResult<T> {
  data: T[];
  meta: PageMeta;
}
